import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChecklistResolveResponse, useCatalogApi } from '../catalog/catalog-api';
import type {
  ChecklistEntry,
  ChecklistSubsetDefinition,
  ChecklistParallelDefinition,
  ChecklistParallelGroup,
} from '../shared/types';
import {
  isChecklistPlaceholderLabel,
  decideChecklistParallelApplicability,
  parseChecklistSourceProductId,
  type ChecklistSubsetPolicy,
} from '@tcvs/shared';
import { checklistRosterSubset } from '../catalog/checklist-parallel-selection';

export const subsetKey = (value: string | undefined): string => (value ?? '').trim();

/** Reference definitions are options, never fabricated roster cards. */
export function checklistSubsetGroups(data: ChecklistResolveResponse): ChecklistParallelGroup[] {
  const groups = new Map<string, ChecklistParallelGroup>();
  const add = (name: string, definitions: ChecklistParallelDefinition[] = []) => {
    if (!name.trim()) return;
    const key = subsetKey(name);
    const current = groups.get(key) ?? { subset: name.trim(), definitions: [] };
    const merged = new Map(
      current.definitions.map((definition) => [definition.definitionId, definition]),
    );
    for (const definition of definitions) merged.set(definition.definitionId, definition);
    groups.set(key, { ...current, definitions: [...merged.values()] });
  };
  for (const definition of data.subsetDefinitions ?? []) add(definition.name);
  if (data.parallelMatch?.tier === 3) {
    for (const group of data.parallelMatch.groups) add(group.subset, group.definitions);
  }
  for (const definition of data.parallelDefinitions ?? []) {
    if (definition.applicability.subset?.trim()) add(definition.applicability.subset, [definition]);
  }
  return [...groups.values()].sort((a, b) => a.subset.localeCompare(b.subset));
}

export function subsetDefinitionsForCard(
  definitions: readonly ChecklistParallelDefinition[],
  subset: string,
  number: string,
  familyId?: string,
  source?: { sourceSystem: string; sourceTargetId: string },
): ChecklistParallelDefinition[] {
  if (!source) return [];
  return definitions.filter(
    (definition) =>
      !isChecklistPlaceholderLabel(definition.name) &&
      decideChecklistParallelApplicability(
        { ...source, subset, number, familyId, rosterPresent: true },
        definition,
      ).decision === 'allowed',
  );
}

export function allSubsetOptions(
  groups: readonly ChecklistParallelGroup[],
  candidates: readonly ChecklistEntry[],
  current: string,
): Array<{ value: string; label: string }> {
  const names = new Map(groups.map((group) => [subsetKey(group.subset), group.subset]));
  for (const entry of candidates) {
    const name = checklistRosterSubset(entry);
    if (name && !names.has(subsetKey(name))) names.set(subsetKey(name), name);
  }
  if (current.trim() && !names.has(subsetKey(current)))
    names.set(subsetKey(current), current.trim());
  return [...names.values()]
    .sort((a, b) => a.localeCompare(b))
    .map((name) => ({ value: name, label: name }));
}

type Resolver = ReturnType<typeof useCatalogApi>['checklistResolve'];
type CatalogState = {
  status: 'loading' | 'ready' | 'error';
  groups: ChecklistParallelGroup[];
  subsetDefinitions: ChecklistSubsetDefinition[];
  error?: string;
};

/** Cache only checklist-wide metadata; late responses cannot change another checklist or any draft. */
export function useChecklistSubsets(
  resolve: Resolver,
  cardId: string | null,
  checklistId: string | null,
  enabled: boolean,
  selected?: { visit: number; subset: string; number: string },
) {
  const [states, setStates] = useState<Record<string, CatalogState>>({});
  const [policyState, setPolicyState] = useState<{
    scope: string;
    policy?: ChecklistSubsetPolicy;
    error?: string;
    loading: boolean;
  }>({ scope: '', loading: false });
  const [policyRetry, setPolicyRetry] = useState(0);
  const policyScope = JSON.stringify([
    cardId,
    checklistId,
    selected?.visit,
    selected?.subset,
    selected?.number,
    enabled,
    policyRetry,
  ]);
  const latestPolicyScope = useRef(policyScope);
  latestPolicyScope.current = policyScope;
  useEffect(() => {
    const scope = policyScope;
    if (!enabled || cardId === null || checklistId === null || !selected?.subset.trim()) {
      setPolicyState({ scope, loading: false });
      return;
    }
    let live = true;
    setPolicyState({ scope, loading: true });
    void resolve(cardId, {
      checklistId,
      subset: selected.subset.trim(),
      ...(selected.number.trim() ? { searchNumber: selected.number.trim() } : {}),
      browseSubsets: true,
    })
      .then((result) => {
        if (!live || latestPolicyScope.current !== scope) return;
        if (!result.ok) throw new Error('policy unavailable');
        const policy = result.data.subsetPolicy;
        const source = parseChecklistSourceProductId(checklistId);
        if (
          !policy ||
          typeof policy.revision !== 'string' ||
          !policy.revision.trim() ||
          !['allowed', 'requires-parallel', 'unknown'].includes(policy.plainVersion?.decision) ||
          (policy.subsetDefinition &&
            (!source ||
              policy.subsetDefinition.name.trim() !== selected.subset.trim() ||
              policy.subsetDefinition.source.system !== source.system ||
              policy.subsetDefinition.source.targetId !== source.targetId))
        ) {
          setPolicyState({
            scope,
            loading: false,
            error:
              'Current subset policy is unavailable. Select an exact current checklist subset, retry, or use explicit manual review.',
          });
          return;
        }
        setPolicyState({ scope, loading: false, policy });
      })
      .catch(() => {
        if (live && latestPolicyScope.current === scope)
          setPolicyState({
            scope,
            loading: false,
            error: 'Current subset policy could not be loaded. Your identity is unchanged.',
          });
      });
    return () => {
      live = false;
    };
    // Exact immutable scope captures every selected source/printed-number/visit input.
  }, [resolve, policyScope]);
  const requests = useRef(new Map<string, number>());
  const sequence = useRef(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      requests.current.clear();
    };
  }, []);
  const load = useCallback(
    async (id: string, checklist: string) => {
      const token = ++sequence.current;
      requests.current.set(checklist, token);
      setStates((previous) => ({
        ...previous,
        [checklist]: {
          status: 'loading',
          groups: previous[checklist]?.groups ?? [],
          subsetDefinitions: previous[checklist]?.subsetDefinitions ?? [],
        },
      }));
      try {
        const result = await resolve(id, { checklistId: checklist, browseSubsets: true });
        if (!mounted.current || requests.current.get(checklist) !== token) return;
        if (!result.ok) throw new Error(result.message ?? result.error);
        setStates((previous) => ({
          ...previous,
          [checklist]: {
            status: 'ready',
            groups: checklistSubsetGroups(result.data),
            subsetDefinitions: result.data.subsetDefinitions ?? [],
          },
        }));
      } catch {
        if (!mounted.current || requests.current.get(checklist) !== token) return;
        setStates((previous) => ({
          ...previous,
          [checklist]: {
            status: 'error',
            subsetDefinitions: previous[checklist]?.subsetDefinitions ?? [],
            groups: previous[checklist]?.groups ?? [],
            error: 'All subsets could not be loaded. Your current selection is unchanged.',
          },
        }));
      }
    },
    [resolve],
  );
  useEffect(() => {
    if (
      enabled &&
      cardId !== null &&
      checklistId !== null &&
      states[checklistId] === undefined &&
      !requests.current.has(checklistId)
    )
      void load(cardId, checklistId);
  }, [cardId, checklistId, enabled, load, states]);
  const retry = useCallback(() => {
    if (cardId !== null && checklistId !== null) void load(cardId, checklistId);
  }, [cardId, checklistId, load]);
  const invalidate = useCallback(() => {
    requests.current.clear();
    setStates({});
    setPolicyRetry((value) => value + 1);
  }, []);
  const state = checklistId === null ? undefined : states[checklistId];
  return {
    subsetPolicy: policyState.scope === policyScope ? policyState.policy : undefined,
    policyLoading: policyState.scope !== policyScope || policyState.loading,
    policyError: policyState.scope === policyScope ? policyState.error : undefined,
    retryPolicy: () => setPolicyRetry((value) => value + 1),
    groups: state?.groups ?? [],
    subsetDefinitions: state?.subsetDefinitions ?? [],
    loading: enabled && (state === undefined || state.status === 'loading'),
    error: state?.error,
    retry,
    invalidate,
  };
}
